import { useUpdateInquiry } from '@inquiries/-hooks/useUpdateInquiry'
import { evaluateInquiryStatus } from '@inquiries/-lib/evaluate-inquiry-status'
import type { GetInquiryAssignsResponse, InquiryAssignResponse } from 'backend/types'
import { useCallback, useState } from 'react'
import { INQUIRY_COMPLETED_STATUS_IDS } from 'shared'

function buildNextStatusIds(
  assigns: InquiryAssignResponse[],
  savedAssignStatusId: number,
  editingAssignId: number | undefined,
): number[] {
  if (editingAssignId === undefined) {
    return [...assigns.map((a) => a.inquiry_assign_status_id), savedAssignStatusId]
  }
  return assigns.map((a) => (a.id === editingAssignId ? savedAssignStatusId : a.inquiry_assign_status_id))
}

interface UseInquiryStatusSuggestionParams {
  inquiryId: number
  currentInquiryStatusId: number
  assignsData: GetInquiryAssignsResponse | undefined
}

/**
 * アサイン編集にリンクして引合ステータスの変更提案ダイアログを制御する
 */
export function useInquiryStatusSuggestion({
  inquiryId,
  currentInquiryStatusId,
  assignsData,
}: UseInquiryStatusSuggestionParams) {
  const [suggestedStatusId, setSuggestedStatusId] = useState<number | null>(null)
  const clear = useCallback(() => setSuggestedStatusId(null), [])
  const { mutate: updateInquiryStatus, isPending } = useUpdateInquiry(inquiryId, {
    onSuccess: clear,
    onError: clear,
  })

  /**
   * 保存したアサインを反映した仮想一覧で評価し、提案引合ステータスIDを更新する。
   * 引合が確定 / 提案不要 / 現状一致のいずれかなら早期リターン。
   */
  const evaluate = useCallback(
    (savedAssignStatusId: number, editingAssignId: number | undefined) => {
      if (!assignsData || INQUIRY_COMPLETED_STATUS_IDS.has(currentInquiryStatusId)) return

      const nextStatusIds = buildNextStatusIds(assignsData.inquiry_assigns, savedAssignStatusId, editingAssignId)
      const suggested = evaluateInquiryStatus(nextStatusIds)
      if (suggested === null || suggested === currentInquiryStatusId) return

      setSuggestedStatusId(suggested)
    },
    [assignsData, currentInquiryStatusId],
  )

  const confirm = useCallback(() => {
    if (suggestedStatusId !== null) updateInquiryStatus({ inquiryAssignStatusId: suggestedStatusId })
  }, [suggestedStatusId, updateInquiryStatus])

  return {
    suggestedStatusId,
    isPending,
    evaluate,
    clear,
    confirm,
  }
}
